<template>
  <div
    class="constructor-work-item"
    :class="{ 'constructor-work-item--active': active }"
    @click="$emit('activate')"
  >
    <!-- Header -->
    <div class="constructor-work-item-header">
      <span
        class="constructor-work-item-toggle"
        title="Свернуть / Развернуть"
        @click.stop="$emit('toggle-collapse')"
      >
        <feather-icon
          :icon="item.collapsed ? 'ChevronRightIcon' : 'ChevronDownIcon'"
          size="14"
        />
      </span>
      <span class="constructor-work-item-cmd">{{ item.command }}</span>
      <span
        class="constructor-work-item-open"
        title="Открыть объект"
        @click.stop="openObject"
      >
        <feather-icon
          icon="ExternalLinkIcon"
          size="14"
        />
      </span>
      <span
        class="constructor-work-item-close"
        title="Убрать"
        @click.stop="$emit('remove')"
      >
        <feather-icon
          icon="XIcon"
          size="14"
        />
      </span>
    </div>

    <template v-if="!item.collapsed">
      <div class="constructor-work-item-body">
        <div class="constructor-obj-tree">
          <div
            class="constructor-obj-tree-root"
            :class="{ 'constructor-obj-tree-focused': focusedNodeIndex === 0 }"
            @click="selectNode(0); $emit('toggle-expand')"
          >
            <feather-icon
              :icon="item.expanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
              size="14"
              class="constructor-tree-toggle"
            />
            <feather-icon
              icon="BoxIcon"
              size="14"
              class="constructor-tree-icon"
            />
            <b-form-input
              v-if="editingName"
              ref="nameInput"
              v-model="editNameValue"
              size="sm"
              class="constructor-obj-tree-name-input"
              @keydown.enter.prevent="saveName"
              @keydown.esc="cancelName"
              @blur="saveName"
              @click.native.stop
            />
            <span
              v-else
              class="constructor-obj-tree-name"
              @dblclick.stop="startEditName"
            >
              {{ item.command }}
            </span>
            <span class="constructor-obj-tree-sid">{{ item.sid }}</span>
            <span class="constructor-obj-tree-id">#{{ item.objectId }}</span>
            <span
              v-if="item.metaName"
              class="constructor-obj-tree-meta"
            >
              {{ item.metaName }}
            </span>
          </div>
          <template v-if="item.expanded">
            <div class="constructor-obj-tree-children">
              <div
                class="constructor-obj-tree-folder"
                :class="{ 'constructor-obj-tree-focused': focusedNodeIndex === 1 }"
                @click.stop="selectNode(1); $emit('toggle-attrs')"
              >
                <feather-icon
                  :icon="item.attrsExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                  size="14"
                  class="constructor-tree-toggle"
                />
                <feather-icon
                  icon="FolderIcon"
                  size="14"
                  class="constructor-tree-icon"
                />
                <span class="constructor-obj-tree-folder-name">Атрибуты</span>
                <span
                  v-if="item.attributes"
                  class="constructor-tree-meta-count"
                >
                  {{ item.attributes.length }}
                </span>
                <b-spinner
                  v-if="item.attrsLoading"
                  small
                  variant="primary"
                  class="ml-1"
                />
                <span
                  class="constructor-obj-tree-add-btn"
                  title="Добавить атрибут"
                  @click.stop="openAddAttr"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="14"
                  />
                </span>
              </div>
              <div
                v-if="addAttrOpen"
                class="constructor-obj-tree-add-panel"
              >
                <b-form-input
                  ref="addAttrInput"
                  v-model="addAttrSearch"
                  size="sm"
                  placeholder="Поиск сущности..."
                  autocomplete="off"
                  @input="filterEntities"
                  @keydown.esc="closeAddAttr"
                  @keydown.enter.prevent="selectFirstEntity"
                />
                <div
                  v-if="filteredEntities.length > 0"
                  class="constructor-obj-tree-entity-list"
                >
                  <div
                    v-for="ent in filteredEntities"
                    :key="ent.id"
                    class="constructor-obj-tree-entity-item"
                    @click.stop="addAttr(ent)"
                  >
                    <span class="constructor-obj-tree-entity-name">{{ ent.name }}</span>
                    <span class="constructor-obj-tree-entity-sid">{{ ent.sid }}</span>
                  </div>
                </div>
                <div
                  v-else-if="addAttrSearch && !entitiesLoading"
                  class="constructor-obj-tree-entity-empty"
                >
                  Не найдено
                </div>
              </div>
              <div
                v-if="item.attrsExpanded && item.objectFull && item.objectFull.attributes"
                class="constructor-obj-tree-attrs"
              >
                <cbs-constructor-attr-card
                  v-for="(attr, attrIdx) in item.objectFull.attributes"
                  :key="attr.attributeid || attr.id"
                  :attr="attr"
                  :expanded="!!expandedAttrs[attrIdx]"
                  :focused="isFocusedAttr(attrIdx)"
                  :focused-prop-index="focusedAttrPropIndex(attrIdx)"
                  :prop-keys="attrPropKeys(attr)"
                  @select="selectNode(attrNodeIndex(attrIdx))"
                  @toggle="toggleAttrProps(attrIdx)"
                  @select-prop="pi => selectNode(attrNodeIndex(attrIdx) + 1 + pi)"
                  @update-field="payload => $emit('update-attr', { attributeid: attr.attributeid, attr, ...payload })"
                  @toggle-delete="$emit('toggle-delete-attr', { attr })"
                  @remove="$emit('remove-attr', { attr })"
                />
              </div>
              <!-- Steps folder (mount objects only) -->
              <template v-if="isMount">
                <div
                  class="constructor-obj-tree-folder"
                  :class="{ 'constructor-obj-tree-focused': focusedNodeIndex === stepsNodeIndex }"
                  @click.stop="selectNode(stepsNodeIndex); toggleSteps()"
                >
                  <feather-icon
                    :icon="stepsExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                    size="14"
                    class="constructor-tree-toggle"
                  />
                  <feather-icon
                    icon="PlayIcon"
                    size="14"
                    class="constructor-tree-icon"
                  />
                  <span class="constructor-obj-tree-folder-name">Шаги</span>
                  <span
                    v-if="steps.length > 0"
                    class="constructor-tree-meta-count"
                  >
                    {{ steps.length }}
                  </span>
                  <span
                    class="constructor-obj-tree-add-btn"
                    title="Добавить шаг"
                    @click.stop="$emit('add-step')"
                  >
                    <feather-icon
                      icon="PlusIcon"
                      size="12"
                    />
                  </span>
                  <b-spinner
                    v-if="stepsLoading"
                    small
                    variant="primary"
                    class="ml-1"
                  />
                </div>
                <div
                  v-if="stepsExpanded && steps.length > 0"
                  class="constructor-obj-tree-attrs"
                >
                  <cbs-constructor-step-card
                    v-for="(step, stepIdx) in steps"
                    :key="'s-' + step.id"
                    :step="step"
                    :step-idx="stepIdx"
                    :expanded="!!expandedSteps[stepIdx]"
                    :filter-expanded="!!filterExpandedSteps[stepIdx]"
                    :json-expanded="getStepJsonExpanded(stepIdx)"
                    :focused="isFocusedStep(stepIdx)"
                    :focused-prop-index="focusedStepPropIndex(stepIdx)"
                    :prop-keys="stepPropKeys(step)"
                    :operand-options="operandOptions"
                    :object-ref="objectRef"
                    :fields="stepFilterFields(step)"
                    :dest-fields="stepObjectFields(step.destobjectId)"
                    :source-fields="stepObjectFields(step.sourceobjectId)"
                    :param-fields="mountParamFields"
                    @select="selectNode(stepNodeIndex(stepIdx))"
                    @toggle="toggleStepProps(stepIdx)"
                    @select-prop="pi => selectNode(stepNodeIndex(stepIdx) + 1 + pi)"
                    @update-field="payload => onStepFieldChange(step, payload.field, payload.value, payload.title)"
                    @toggle-delete="onStepToggleDelete(step)"
                    @toggle-filter="toggleStepFilter(stepIdx)"
                    @toggle-json="field => toggleStepJsonProp(stepIdx, field)"
                    @reorder="onStepReorder"
                    @add-work-object="objectId => $emit('add-work-object', objectId)"
                    @load-object-fields="stepObjectFields"
                  />
                </div>
              </template>
            </div>
          </template>
        </div>

        <!-- Form Designer (for form objects) -->
        <div
          v-if="isForm && formDesignerOpen"
          class="constructor-form-designer-wrap"
        >
          <cbs-form-designer
            :object-full="item.objectFull"
            :all-entities="allEntities"
            :all-objects="allObjects"
          />
        </div>
        <div
          v-if="isForm && !formDesignerOpen"
          class="constructor-form-designer-btn"
          @click="formDesignerOpen = true"
        >
          <feather-icon
            icon="LayoutIcon"
            size="14"
            class="mr-50"
          />
          Открыть конструктор формы
        </div>

        <!-- Reference Availability — only for catalog objects, shows rules restricting THIS catalog -->
        <div
          v-if="isCatalog && refAvailOpen"
          class="constructor-ref-avail-wrap"
        >
          <cbs-ref-availability
            :embedded="true"
            :filter-ref-object-id="item.objectId"
          />
        </div>
        <div
          v-if="isCatalog && !refAvailOpen"
          class="constructor-form-designer-btn"
          @click="refAvailOpen = true"
        >
          <feather-icon
            icon="ShieldIcon"
            size="14"
            class="mr-50"
          />
          Доступность этого справочника (кому/где какие записи доступны)
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { BFormInput, BSpinner } from 'bootstrap-vue'
import CbsConstructorAttrCard from './CbsConstructorAttrCard.vue'
import CbsConstructorStepCard from './CbsConstructorStepCard.vue'
import CbsFormDesigner from './form-designer/CbsFormDesigner.vue'
import CbsRefAvailability from './CbsRefAvailability.vue'

export default {
  name: 'CbsConstructorObjectCard',
  components: {
    CbsConstructorAttrCard,
    CbsConstructorStepCard,
    CbsFormDesigner,
    CbsRefAvailability,
    BFormInput,
    BSpinner,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
    allEntities: {
      type: Array,
      default: () => [],
    },
    entitiesLoading: {
      type: Boolean,
      default: false,
    },
    mountOperands: {
      type: Array,
      default: () => [],
    },
    allObjects: {
      type: Array,
      default: () => [],
    },
    objectFieldsCache: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      editingName: false,
      editNameValue: '',
      addAttrOpen: false,
      addAttrSearch: '',
      filteredEntities: [],
      focusedNodeIndex: -1,
      expandedAttrs: {},
      stepsExpanded: false,
      stepsLoading: false,
      steps: [],
      expandedSteps: {},
      filterExpandedSteps: {},
      jsonExpandedProps: {},
      formDesignerOpen: false,
      refAvailOpen: false,
    }
  },
  computed: {
    isMount() {
      return this.item.objectFull
        && this.item.objectFull.object
        && this.item.objectFull.object.meta === 'mount'
    },
    isForm() {
      return this.item.objectFull
        && this.item.objectFull.object
        && this.item.objectFull.object.meta === 'form'
    },
    isCatalog() {
      return this.item.objectFull
        && this.item.objectFull.object
        && this.item.objectFull.object.meta === 'catalog'
    },
    flatNodes() {
      const nodes = []
      // 0: root (name + sid + id)
      nodes.push({ type: 'root' })
      if (this.item.expanded) {
        // 1: attrs folder
        nodes.push({ type: 'attrs-folder' })
        if (this.item.attrsExpanded && this.item.objectFull && this.item.objectFull.attributes) {
          this.item.objectFull.attributes.forEach((attr, attrIdx) => {
            nodes.push({ type: 'attr', attr, attrIdx })
            if (this.expandedAttrs[attrIdx]) {
              this.attrPropKeys(attr).forEach(pk => {
                nodes.push({
                  type: 'attr-prop',
                  attr,
                  attrIdx,
                  propKey: pk.key,
                  propVal: pk.val,
                  editable: pk.editable || false,
                  field: pk.field || null,
                })
              })
            }
          })
        }
        // steps folder (only for mount objects)
        if (this.isMount) {
          nodes.push({ type: 'steps-folder' })
          if (this.stepsExpanded && this.steps.length > 0) {
            this.steps.forEach((step, stepIdx) => {
              nodes.push({ type: 'step', step, stepIdx })
              if (this.expandedSteps[stepIdx]) {
                this.stepPropKeys(step).forEach(pk => {
                  nodes.push({
                    type: 'step-prop',
                    step,
                    stepIdx,
                    propKey: pk.key,
                    propVal: pk.val,
                    editable: pk.editable || false,
                    field: pk.field || null,
                  })
                })
              }
            })
          }
        }
      }
      return nodes
    },
    nodeCount() {
      return this.flatNodes.length
    },
    stepsNodeIndex() {
      return this.flatNodes.findIndex(n => n.type === 'steps-folder')
    },
    objectRef() {
      return this.allObjects.map(o => ({ value: o.id, title: o.name }))
    },
    operandOptions() {
      return this.mountOperands.map(o => ({ value: o.id, text: o.name }))
    },
    mountParamFields() {
      const objectId = this.item && this.item.objectId
      if (!objectId) return []
      const cached = this.objectFieldsCache[objectId]
      if (!cached) {
        this.$emit('load-object-fields', objectId)
        return []
      }
      return cached.fields || []
    },
  },
  watch: {
    allEntities() {
      if (this.addAttrOpen && this.addAttrSearch.trim()) {
        this.filterEntities()
      }
    },
    active(val) {
      if (!val) this.focusedNodeIndex = -1
    },
    isMount(val) {
      if (val && this.steps.length === 0 && !this.stepsLoading) {
        this.loadSteps()
      }
      if (val) {
        this.$emit('ensure-operands')
      }
    },
  },
  mounted() {
    this.keyHandler = e => this.onTreeKeydown(e)
    document.addEventListener('keydown', this.keyHandler)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.keyHandler)
  },
  methods: {
    onTreeKeydown(e) {
      if (!this.active || this.focusedNodeIndex < 0) return
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      if (e.target.closest('.constructor-tree')) return
      const { key } = e
      if (key === 'ArrowDown') {
        e.preventDefault()
        if (this.focusedNodeIndex < this.nodeCount - 1) {
          this.focusedNodeIndex += 1
          this.scrollToFocused()
        }
      } else if (key === 'ArrowUp') {
        e.preventDefault()
        if (this.focusedNodeIndex > 0) {
          this.focusedNodeIndex -= 1
          this.scrollToFocused()
        }
      } else if (key === 'ArrowRight') {
        e.preventDefault()
        const node = this.flatNodes[this.focusedNodeIndex]
        if (!node) return
        if (node.type === 'root' && !this.item.expanded) {
          this.$emit('toggle-expand')
        } else if (node.type === 'attrs-folder' && !this.item.attrsExpanded) {
          this.$emit('toggle-attrs')
        } else if (node.type === 'attr' && !this.expandedAttrs[node.attrIdx]) {
          this.toggleAttrProps(node.attrIdx)
        } else if (node.type === 'steps-folder' && !this.stepsExpanded) {
          this.toggleSteps()
        } else if (node.type === 'step' && !this.expandedSteps[node.stepIdx]) {
          this.toggleStepProps(node.stepIdx)
        } else if (node.type === 'step-prop' && node.editable === 'filter' && !this.filterExpandedSteps[node.stepIdx]) {
          this.toggleStepFilter(node.stepIdx)
        } else if (node.type === 'step-prop' && node.editable === 'json') {
          const expanded = this.jsonExpandedProps[node.stepIdx] && this.jsonExpandedProps[node.stepIdx][node.field]
          if (!expanded) this.toggleStepJsonProp(node.stepIdx, node.field)
        }
      } else if (key === 'ArrowLeft') {
        e.preventDefault()
        const node = this.flatNodes[this.focusedNodeIndex]
        if (!node) return
        if (node.type === 'root' && this.item.expanded) {
          this.$emit('toggle-expand')
        } else if (node.type === 'attrs-folder' && this.item.attrsExpanded) {
          this.$emit('toggle-attrs')
        } else if (node.type === 'steps-folder' && this.stepsExpanded) {
          this.stepsExpanded = false
        } else if (node.type === 'attr' && this.expandedAttrs[node.attrIdx]) {
          this.toggleAttrProps(node.attrIdx)
        } else if (node.type === 'attr' && !this.expandedAttrs[node.attrIdx]) {
          this.focusedNodeIndex = 1
          this.scrollToFocused()
        } else if (node.type === 'attr-prop') {
          const parentIdx = this.flatNodes.findIndex(n => n.type === 'attr' && n.attrIdx === node.attrIdx)
          if (parentIdx >= 0) {
            this.focusedNodeIndex = parentIdx
            this.scrollToFocused()
          }
        } else if (node.type === 'step' && this.expandedSteps[node.stepIdx]) {
          this.toggleStepProps(node.stepIdx)
        } else if (node.type === 'step' && !this.expandedSteps[node.stepIdx]) {
          this.focusedNodeIndex = this.stepsNodeIndex
          this.scrollToFocused()
        } else if (node.type === 'step-prop' && node.editable === 'filter' && this.filterExpandedSteps[node.stepIdx]) {
          this.toggleStepFilter(node.stepIdx)
        } else if (node.type === 'step-prop' && node.editable === 'json') {
          const expanded = this.jsonExpandedProps[node.stepIdx] && this.jsonExpandedProps[node.stepIdx][node.field]
          if (expanded) {
            this.toggleStepJsonProp(node.stepIdx, node.field)
          } else {
            const parentIdx = this.flatNodes.findIndex(n => n.type === 'step' && n.stepIdx === node.stepIdx)
            if (parentIdx >= 0) {
              this.focusedNodeIndex = parentIdx
              this.scrollToFocused()
            }
          }
        } else if (node.type === 'step-prop') {
          const parentIdx = this.flatNodes.findIndex(n => n.type === 'step' && n.stepIdx === node.stepIdx)
          if (parentIdx >= 0) {
            this.focusedNodeIndex = parentIdx
            this.scrollToFocused()
          }
        } else if (node.type === 'attrs-folder' || node.type === 'steps-folder') {
          this.focusedNodeIndex = 0
          this.scrollToFocused()
        }
      } else if (key === 'Enter' || key === ' ') {
        e.preventDefault()
        const node = this.flatNodes[this.focusedNodeIndex]
        if (!node) return
        if (node.type === 'root') {
          this.$emit('toggle-expand')
        } else if (node.type === 'attrs-folder') {
          this.$emit('toggle-attrs')
        } else if (node.type === 'steps-folder') {
          this.toggleSteps()
        } else if (node.type === 'attr') {
          this.toggleAttrProps(node.attrIdx)
        } else if (node.type === 'attr-prop' && node.editable) {
          this.$emit('update-attr', {
            attributeid: node.attr.attributeid,
            attr: node.attr,
            field: node.field,
            value: !node.propVal,
          })
        } else if (node.type === 'step') {
          this.toggleStepProps(node.stepIdx)
        }
      }
    },
    scrollToFocused() {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.constructor-obj-tree-focused, .constructor-attr-focused, .constructor-step-focused')
        if (el) {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },
    blurActiveInput() {
      if (document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
        document.activeElement.blur()
      }
    },
    selectNode(index) {
      this.focusedNodeIndex = index
      this.blurActiveInput()
    },
    startEditName() {
      this.editNameValue = this.item.command || ''
      this.editingName = true
      this.$nextTick(() => {
        const input = this.$refs.nameInput
        if (input) input.focus()
      })
    },
    saveName() {
      if (!this.editingName) return
      this.editingName = false
      const val = this.editNameValue.trim()
      if (val && val !== this.item.command) {
        this.$emit('rename', val)
      }
    },
    cancelName() {
      this.editingName = false
    },
    focusFirstAttr() {
      // root is at index 0, focus it to show sid/id
      this.focusedNodeIndex = 0
    },
    openAddAttr() {
      if (this.addAttrOpen) {
        this.closeAddAttr()
        return
      }
      this.addAttrOpen = true
      this.addAttrSearch = ''
      this.filteredEntities = []
      this.$emit('ensure-entities')
      this.$nextTick(() => {
        const input = this.$refs.addAttrInput
        if (input) input.focus()
      })
    },
    closeAddAttr() {
      this.addAttrOpen = false
      this.addAttrSearch = ''
      this.filteredEntities = []
    },
    getExistingEntityIds() {
      if (!this.item.objectFull || !this.item.objectFull.attributes) return []
      return this.item.objectFull.attributes.map(a => {
        if (a.entity) return a.entity.id
        if (a.entityid) return a.entityid
        return a.id
      })
    },
    filterEntities() {
      if (!this.addAttrSearch.trim()) {
        this.filteredEntities = []
        return
      }
      const parts = this.addAttrSearch.trim().toLowerCase().split(/\s+/)
      const escaped = parts.map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      const regex = new RegExp(escaped.join('.*'))
      const existingIds = this.getExistingEntityIds()
      this.filteredEntities = this.allEntities
        .filter(e => !existingIds.includes(e.id)
          && ((e.name && regex.test(e.name.toLowerCase())) || (e.sid && regex.test(e.sid.toLowerCase()))))
        .slice(0, 20)
    },
    selectFirstEntity() {
      if (this.filteredEntities.length > 0) {
        this.addAttr(this.filteredEntities[0])
      }
    },
    addAttr(entity) {
      this.$emit('add-attr', entity)
      this.closeAddAttr()
    },
    attrPropKeys(attr) {
      const props = []
      props.push({ key: 'key', val: attr.key })
      props.push({ key: 'entitysid', val: attr.entitysid })
      props.push({ key: 'entityid', val: attr.entityid })
      props.push({ key: 'datatype', val: attr.datatype })
      if (attr.refobjectid) props.push({ key: 'refobjectid', val: attr.refobjectid })
      props.push({
        key: 'required', val: attr.isrequired, editable: true, field: 'isrequired',
      })
      props.push({
        key: 'unique', val: attr.isunique, editable: true, field: 'isunique',
      })
      if (attr.inherited) props.push({ key: 'inherited', val: attr.inherited })
      if (attr.rendertype) props.push({ key: 'rendertype', val: attr.rendertype })
      if (attr.properties && attr.properties.length) {
        props.push({ key: 'properties', val: attr.properties.length })
        attr.properties.forEach(p => {
          const v = (p.argument && p.argument.argvalue) ? (p.argument.argvalue.title || p.argument.argvalue.value) : ''
          props.push({ key: `  ${p.parameter}`, val: v })
        })
      }
      return props
    },
    attrNodeIndex(attrIdx) {
      return this.flatNodes.findIndex(n => n.type === 'attr' && n.attrIdx === attrIdx)
    },
    isFocusedAttr(attrIdx) {
      const ni = this.attrNodeIndex(attrIdx)
      return ni >= 0 && this.focusedNodeIndex === ni
    },
    focusedAttrPropIndex(attrIdx) {
      const node = this.flatNodes[this.focusedNodeIndex]
      if (!node || node.type !== 'attr-prop' || node.attrIdx !== attrIdx) return -1
      const attrNi = this.attrNodeIndex(attrIdx)
      return this.focusedNodeIndex - attrNi - 1
    },
    toggleAttrProps(attrIdx) {
      this.$set(this.expandedAttrs, attrIdx, !this.expandedAttrs[attrIdx])
    },
    toggleSteps() {
      this.stepsExpanded = !this.stepsExpanded
      if (this.stepsExpanded && this.steps.length === 0 && !this.stepsLoading) {
        this.loadSteps()
      }
    },
    toggleStepProps(stepIdx) {
      this.$set(this.expandedSteps, stepIdx, !this.expandedSteps[stepIdx])
    },
    toggleStepFilter(stepIdx) {
      this.$set(this.filterExpandedSteps, stepIdx, !this.filterExpandedSteps[stepIdx])
    },
    getStepJsonExpanded(stepIdx) {
      return this.jsonExpandedProps[stepIdx] || {}
    },
    toggleStepJsonProp(stepIdx, field) {
      if (!this.jsonExpandedProps[stepIdx]) {
        this.$set(this.jsonExpandedProps, stepIdx, {})
      }
      this.$set(this.jsonExpandedProps[stepIdx], field, !this.jsonExpandedProps[stepIdx][field])
    },
    stepPropKeys(step) {
      const props = []
      props.push({ key: 'id', val: step.id })
      if (step.descr) props.push({ key: 'descr', val: step.descr })
      props.push({
        key: 'oper', val: step.operId, displayVal: step.oper, editable: 'select', field: 'mountoperid',
      })
      props.push({
        key: 'dest', val: step.destobjectId, displayVal: step.destobject, editable: 'select', field: 'destobjectid', options: 'objects',
      })
      if (step.destscenario) props.push({ key: 'dst scenario', val: step.destscenario })
      props.push({
        key: 'source', val: step.sourceobjectId, displayVal: step.sourceobject, editable: 'select', field: 'sourceobjectid', options: 'objects',
      })
      if (step.sourcescenario) props.push({ key: 'src scenario', val: step.sourcescenario })
      props.push({
        key: 'filter', editable: 'filter', field: 'jsonfilter', jsonObj: this.getStepJsonObj(step, 'jsonfilter'),
      })
      props.push({
        key: 'mod', editable: 'json', field: 'jsonmod', jsonObj: this.getStepJsonObj(step, 'jsonmod'), renderType: 'jsonmodification',
      })
      props.push({
        key: 'groupby', editable: 'json', field: 'jsongroupby', jsonObj: this.getStepJsonObj(step, 'jsongroupby'), renderType: 'jsongroupby',
      })
      props.push({
        key: 'join', editable: 'json', field: 'jsonjoin', jsonObj: this.getStepJsonObj(step, 'jsonjoin'), renderType: 'jsonjoin',
      })
      return props
    },
    focusedStepPropIndex(stepIdx) {
      const node = this.flatNodes[this.focusedNodeIndex]
      if (!node || node.type !== 'step-prop' || node.stepIdx !== stepIdx) return -1
      const stepNi = this.stepNodeIndex(stepIdx)
      return this.focusedNodeIndex - stepNi - 1
    },
    onStepReorder({ fromIdx, toIdx }) {
      const step = this.steps.splice(fromIdx, 1)[0]
      if (!step) return
      this.steps.splice(toIdx, 0, step)
      // reset expanded state (indices shifted)
      this.expandedSteps = {}
      // reassign ordernums sequentially
      const changed = []
      this.steps.forEach((s, i) => {
        const newOrder = (i + 1) * 10
        if (s.ordernum !== newOrder) {
          s.ordernum = newOrder
          changed.push({ stepId: s.id, value: newOrder })
        }
      })
      changed.forEach(c => {
        this.$emit('update-step', { stepId: c.stepId, field: 'ordernum', value: c.value })
      })
    },
    onStepToggleDelete(step) {
      const newDel = !step.del
      const method = newDel ? 'deleterecord' : 'restorerecord'
      this.$emit('toggle-delete-step', {
        stepId: step.id,
        method,
        raw: step._raw,
        callback: success => {
          if (!success) return
          const s = this.steps.find(st => st.id === step.id)
          if (s) {
            this.$set(s, 'del', newDel)
          }
        },
      })
    },
    onStepFieldChange(step, field, value, title) {
      this.$emit('update-step', {
        stepId: step.id,
        field,
        value,
        callback: success => {
          if (!success) return
          const s = this.steps.find(st => st.id === step.id)
          if (!s) return
          if (field === 'name') {
            s.name = value
          } else if (field === 'mountoperid') {
            s.operId = value
            const op = this.mountOperands.find(o => o.id === value)
            s.oper = op ? op.name : value
          } else if (field === 'destobjectid') {
            s.destobjectId = value
            s.destobject = title || (value || '')
          } else if (field === 'sourceobjectid') {
            s.sourceobjectId = value
            s.sourceobject = title || (value || '')
          } else if (field === 'jsonfilter' || field === 'jsonmod' || field === 'jsongroupby' || field === 'jsonjoin') {
            s[field] = typeof value === 'object' ? JSON.stringify(value) : value
          }
        },
      })
    },
    getStepJsonObj(step, field) {
      const cacheKey = `_${field}Obj`
      if (step[cacheKey]) return step[cacheKey]
      let obj = null
      if (step[field]) {
        try {
          obj = typeof step[field] === 'string' ? JSON.parse(step[field]) : step[field]
        } catch (e) {
          obj = null
        }
      }
      if (field === 'jsonfilter' && (!obj || !obj.node)) {
        obj = {
          node: {
            isactive: false,
            isexpanded: false,
            oper: { id: 1, sid: 'and', name: 'AND' },
            nodes: [],
            conds: [],
          },
        }
      }
      if (!obj) obj = {}
      this.$set(step, cacheKey, obj)
      return step[cacheKey]
    },
    stepFilterFields(step) {
      const op = this.mountOperands.find(o => o.id === step.operId)
      const objectId = op && op.sid === 'insert' ? step.sourceobjectId : step.destobjectId
      if (!objectId) return []
      const cached = this.objectFieldsCache[objectId]
      if (!cached) {
        this.$emit('load-object-fields', objectId)
        return []
      }
      return cached.fields || []
    },
    stepObjectFields(objectId) {
      if (!objectId) return []
      const cached = this.objectFieldsCache[objectId]
      if (!cached) {
        this.$emit('load-object-fields', objectId)
        return []
      }
      return cached.fields || []
    },
    loadSteps() {
      this.stepsLoading = true
      this.$emit('load-steps', {
        objectId: this.item.objectId,
        callback: stepsData => {
          this.steps = stepsData
          this.stepsLoading = false
        },
      })
    },
    reloadSteps() {
      this.stepsExpanded = true
      this.loadSteps()
    },
    isFocusedStep(stepIdx) {
      const ni = this.stepNodeIndex(stepIdx)
      return ni >= 0 && this.focusedNodeIndex === ni
    },
    stepNodeIndex(stepIdx) {
      return this.flatNodes.findIndex(n => n.type === 'step' && n.stepIdx === stepIdx)
    },
    openObject() {
      const route = this.$router.resolve({ name: 'cubus-object', params: { objectid: this.item.objectId } })
      window.open(route.href, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
.constructor-work-item {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  overflow: visible;
}

.constructor-work-item-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f8f8f8;
  font-size: 13px;
  font-family: monospace;
  color: #6e6b7b;
  border-bottom: 1px solid #ebe9f1;
}

.constructor-work-item-toggle {
  cursor: pointer;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  color: #b9b9c3;

  &:hover {
    color: #5e5873;
  }
}

.constructor-work-item-cmd {
  font-weight: 600;
  color: #7367f0;
  flex: 1;
}

.constructor-work-item-open {
  cursor: pointer;
  color: #b9b9c3;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;

  &:hover {
    color: #7367f0;
    background: rgba(115, 103, 240, 0.1);
  }
}

.constructor-work-item-close {
  cursor: pointer;
  color: #b9b9c3;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;

  &:hover {
    color: #ea5455;
    background: rgba(234, 84, 85, 0.1);
  }
}

.constructor-work-item--active > .constructor-work-item-header {
  background: #7367f0;
  color: #fff;
  border-color: #7367f0;

  .constructor-work-item-toggle {
    color: rgba(255, 255, 255, 0.7);

    &:hover {
      color: #fff;
    }
  }

  .constructor-work-item-cmd {
    color: #fff;
  }

  .constructor-work-item-open {
    color: rgba(255, 255, 255, 0.7);

    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.15);
    }
  }

  .constructor-work-item-close {
    color: rgba(255, 255, 255, 0.7);

    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.15);
    }
  }
}

.constructor-work-item-body {
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;

  &:focus {
    outline: none;
  }
}

.constructor-obj-tree {
  font-size: 14px;
}

.constructor-obj-tree-root {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
  cursor: pointer;
  user-select: none;
  border-radius: 4px;

  &:hover {
    background: #f8f8f8;
  }
}

.constructor-obj-tree-name {
  font-weight: 600;
  color: #5e5873;
  cursor: default;
}

.constructor-obj-tree-name-input {
  font-size: 13px;
  font-weight: 600;
  height: 22px;
  padding: 0 4px;
  max-width: 200px;
}

.constructor-obj-tree-sid {
  color: #b9b9c3;
  font-size: 12px;
}

.constructor-obj-tree-id {
  color: #b9b9c3;
  font-size: 11px;
}

.constructor-obj-tree-meta {
  font-size: 12px;
  color: #b9b9c3;
  margin-left: 4px;
}

.constructor-obj-tree-children {
  padding-left: 22px;
}

.constructor-obj-tree-prop {
  display: flex;
  gap: 8px;
  padding: 2px 0;
  font-size: 13px;
  border-radius: 4px;
}

.constructor-obj-tree-prop-key {
  color: #b9b9c3;
  min-width: 30px;
}

.constructor-obj-tree-prop-val {
  color: #6e6b7b;
}

.constructor-obj-tree-folder {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
  margin-top: 2px;
  cursor: pointer;
  user-select: none;
  border-radius: 4px;

  &:hover {
    background: #f8f8f8;
  }
}

.constructor-obj-tree-folder-name {
  font-weight: 500;
  color: #5e5873;
}

.constructor-obj-tree-add-btn {
  color: #b9b9c3;
  cursor: pointer;
  padding: 1px 3px;
  border-radius: 3px;
  line-height: 1;
  &:hover { color: #28c76f; background: rgba(40, 199, 111, 0.1); }
}

.constructor-obj-tree-attrs {
  padding-left: 22px;
}

.constructor-obj-tree-focused {
  background: rgba(115, 103, 240, 0.08);
  outline: 1px solid rgba(115, 103, 240, 0.3);
}

.constructor-obj-tree-add-btn {
  margin-left: auto;
  cursor: pointer;
  color: #b9b9c3;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;

  &:hover {
    color: #28c76f;
    background: rgba(40, 199, 111, 0.1);
  }
}

.constructor-obj-tree-add-panel {
  padding: 6px 0 6px 22px;
}

.constructor-obj-tree-entity-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  margin-top: 4px;
}

.constructor-obj-tree-entity-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 8px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #f8f8f8;
  }
}

.constructor-obj-tree-entity-name {
  color: #5e5873;
  font-weight: 500;
}

.constructor-obj-tree-entity-sid {
  color: #b9b9c3;
  font-size: 11px;
}

.constructor-obj-tree-entity-empty {
  padding: 6px 0;
  font-size: 12px;
  color: #b9b9c3;
}

.constructor-tree-toggle {
  color: #b9b9c3;
  flex-shrink: 0;
}

.constructor-tree-icon {
  color: #7367f0;
  flex-shrink: 0;
}

.constructor-tree-meta-count {
  font-size: 12px;
  color: #b9b9c3;
  flex-shrink: 0;
}

// Dark mode
.dark-layout {
  .constructor-work-item {
    border-color: #3b4253;
  }

  .constructor-work-item-header {
    background: #242b3d;
    border-color: #3b4253;
    color: #b4b7bd;
  }

  .constructor-work-item-body {
    color: #d0d2d6;
  }

  .constructor-obj-tree-root:hover,
  .constructor-obj-tree-folder:hover {
    background: #3b4253;
  }

  .constructor-obj-tree-name,
  .constructor-obj-tree-folder-name {
    color: #d0d2d6;
  }

  .constructor-obj-tree-prop-val {
    color: #b4b7bd;
  }

  .constructor-obj-tree-focused {
    background: rgba(115, 103, 240, 0.15);
    outline-color: rgba(115, 103, 240, 0.4);
  }

  .constructor-obj-tree-entity-list {
    border-color: #3b4253;
  }

  .constructor-obj-tree-entity-item:hover {
    background: #3b4253;
  }

  .constructor-obj-tree-entity-name {
    color: #d0d2d6;
  }
}

.constructor-form-designer-wrap {
  margin-top: 12px;
}
.constructor-form-designer-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  margin-top: 8px;
  border: 2px dashed #ddd;
  border-radius: 6px;
  color: #7367f0;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}
.constructor-form-designer-btn:hover {
  border-color: #7367f0;
  background: #f8f7ff;
}
</style>
